// ==========================================================================
// usePrintDocumentV2.js
//
// Renders PDF pages that match the editor 1:1 by measuring every content
// node, then rebuilding each section as a sequence of fixed-size page
// containers with absolutely-positioned content.
//
// Pipeline:
//   1. MEASURE  — measureSection() captures node positions from editor layout
//   2. SNAPSHOT — applyEditorPaginationSnapshot() overlays editor decisions
//   3. PLAN    — buildDecoupageContainers() assigns nodes to pages
//   4. RENDER  — same function positions fragments into containers
//
// Pure helper functions (splitting, measurement, readiness) are in
// usePrintDocumentV2.utils.js — this file contains the stateful hook
// and the orchestration logic that wires everything together.
//
// Watermark defaults, text sanitization, and iframe injection live in
// usePrintDocument.utils.js (shared with legacy usePrintDocument; V2 does not
// import the V1 hook for watermarking).
//
// Diagnostics: planner/empty-decoupage warnings use useLogger and only emit when
// localStorage `_debug.pagination.telemetryLogs` is true (same gate as usePagination).
// Print pipeline and lifecycle errors always use logger.error.
//
// See src/pages/report-workspace/V2/pagination/readme/print-v2-pagination-contract.md
// for the architecture overview.
//
// ⚠️ FRAGILE AREA
// This hook is the last mile between pagination decisions and user-visible PDF.
// Seemingly small edits can introduce subtle regressions (duplicate fragments,
// off-by-one page routing, header overlap, list marker drift) that only show
// up in edge documents. Prefer changing one phase at a time and validate with
// the checklist in the print-v2 contract doc before merging.
// ==========================================================================

import {useCallback, useEffect, useReducer, useRef} from 'react';
import {useReactToPrint} from 'react-to-print';
import _ from 'lodash';

import {PAGE_NUMBER_ALIGN_ITEMS_MAP} from '../constants';
import {
  getContentWidth,
  getContentHeight,
  PORTRAIT_PAGE_HEIGHT,
  PORTRAIT_PAGE_WIDTH,
  getWidowOrphanMinLines,
  flushDocumentLayout,
  getElementDataId,
  isKeepWithNextLikeNode,
  isImageLikeNode,
  isListContainerTag,
  isTextOrListTag,
  isParagraphLikeTag,
  isTextOrListLikeElement,
  PARAGRAPH_TEXT_BLOCK_SELECTOR,
} from '../pages/report-workspace/V2/pagination';
import {useLogger} from '../components/LoggingWrapper';
import {useDebugFlags} from './useDebugFlags';
import {
  DEFAULT_WATERMARK_TEXT,
  injectWatermark,
  sanitizeWatermarkText,
} from './usePrintDocument.utils';
import {
  EXPORT_READY_TIMEOUT_MS,
  EXPORT_READY_MIN_STAGE_TIMEOUT_MS,
  POST_DECOUPAGE_IMAGE_SETTLE_MS,
  CARRY_FRAGMENT_KINDS,
  SOURCE_FRAGMENT_KINDS,
  SOURCE_HEADER_CLAMP_THRESHOLD_PX,
  APPLY_EDITOR_PAGINATION_SNAPSHOT,
  APPLY_EDITOR_SNAPSHOT_GEOMETRY,
  APPLY_EDITOR_CONTRACT_DECISIONS,
  PAGE_BOUNDARY_EPSILON_PX,
} from './usePrintDocumentV2.constants';
import {
  replayScrollStateFromSourceToIframe,
  fixBorderBottomZIndex,
  waitForExportReadiness,
  waitForPostDecoupageImagesReady,
  createMeasurementClone,
  normalizeIframeContent,
  measureSection,
  applyEditorPaginationSnapshot,
  normalizeBoundaryText,
  hasRiskySplitOverlap,
  classifyLineVisibilityAtCutInIframe,
  splitAtContractAnchor,
  splitNodeAtHeight,
  preserveJustification,
  suppressContinuationMarkerAfterListSplit,
  applyScrollPathState,
  cloneWithScrollPaths,
} from './usePrintDocumentV2.utils';

// ─── Hook State ──────────────────────────────────────────────────────────────
// Two execution layers live in this file:
// 1) Hook lifecycle/process control (begin/end/cancel print).
// 2) Decoupage pipeline execution inside react-to-print callback.
//
// Keep them conceptually separate while maintaining behavior:
// - lifecycle regressions usually come from timing/guard logic in begin/end.
// - output regressions usually come from measurement/planning/render steps.

const PrintPhase = {IDLE: 'idle', PRINTING: 'printing'};
const ActionTypes = {BEGIN_PRINT: 'BEGIN_PRINT', RESET: 'RESET'};
const MIN_LOADING_MS = 2000;

const initialState = {phase: PrintPhase.IDLE};

/**
 * Minimal print lifecycle reducer.
 *
 * Why this stays tiny:
 * - Keeping the state machine explicit (`idle` -> `printing` -> `idle`)
 *   makes cancellation/reset paths easy to reason about when async print
 *   callbacks race with user actions.
 */
function printReducer(state, action) {
  switch (action.type) {
    case ActionTypes.BEGIN_PRINT:
      return {...state, phase: PrintPhase.PRINTING};
    case ActionTypes.RESET:
      return initialState;
    default:
      return state;
  }
}

// ─── Planner + Renderer ──────────────────────────────────────────────────────

/**
 * Build per-page containers from pre-measured node data.
 *
 * This is the core of the decoupage algorithm. It runs in two phases:
 *
 * PHASE 1 — PLAN (no DOM mutations)
 *   For each page (0..numPages-1):
 *     a. Process "carried" fragments from the previous page's splitBottoms.
 *     b. Process source nodes assigned to this page.
 *     c. For each node, decide: whole / contractCurrentOnly / contractNextOnly /
 *        contractSplit / inferenceSplit / clipOverflow.
 *     d. Accumulate fragments into `plannedPages[p]`.
 *
 * PHASE 2 — RENDER (read-only plan, write DOM)
 *   For each page:
 *     a. Create a fixed-size container (`print-page`).
 *     b. Create a `contentWrap` div with tiptap styling.
 *     c. Absolutely position each fragment from the plan.
 *     d. Clone header/footer overlays into the container.
 *
 * Content-aware splitting strategy:
 *   - Text (p, h1-h6): split at exact line/character boundary
 *   - Lists (ul, ol):  split at LI boundary; straddle LI gets text-split
 *   - Tables / divs:   clip with overflow:hidden (no mid-cell splitting)
 *
 * Atomic block handling (keep-with-next, images):
 *   - Nodes matching `isKeepWithNextLikeNode` or `isImageLikeNode` are
 *     treated as atomic: they are never split across pages. If such a node
 *     straddles a boundary, the whole block is carried to the next page.
 *   - Carried keep-with-next fragments use kind `keepWithNextCarryWhole`;
 *     carried image fragments use kind `imageCarryWhole`.
 *   - Both are rendered whole on the destination page.
 *
 * List marker suppression:
 *   - Continuation LI fragments (same LI appears on previous page)
 *     have their marker suppressed at split-time AND render-time.
 *   - UL/OL containers that continue across pages at a clean LI boundary
 *     preserve the first LI's marker.
 *
 * Margin zeroing (applyPosition):
 *   - After absolute positioning, marginLeft/marginRight are zeroed so that
 *     authored indent (already captured in measured `left`) is not double-
 *     applied, which would push nodes beyond page bounds.
 *
 * @param {object}   preMeasured    - output of measureSection + applyEditorPaginationSnapshot
 * @param {Document} iframeDoc      - the print iframe's document
 * @param {object}   plannerOptions - `{ strictSplitFidelity?: boolean, widowOrphanMinLines?: number }`
 * @returns {{ containers: Element[], plannerStats: object } | null}
 */
function buildDecoupageContainers(preMeasured, iframeDoc, plannerOptions = {}) {
  if (!preMeasured || !iframeDoc) return null;
  const {strictSplitFidelity = true, widowOrphanMinLines: plannerMinLines} =
    plannerOptions;

  const PAGE_W = preMeasured.pageWidth || PORTRAIT_PAGE_WIDTH;
  const PAGE_H = preMeasured.pageHeight || PORTRAIT_PAGE_HEIGHT;
  const {
    nodes,
    tiptapClassName,
    tiptapInheritedCSS,
    pageStructure,
    authoritativeNodeByRef,
    isLandscape,
  } = preMeasured;
  if (!Array.isArray(pageStructure) || !pageStructure.length) return null;
  if (!Array.isArray(nodes)) return null;
  const numPages = pageStructure.length;
  const widowOrphanMinLines = Number.isFinite(plannerMinLines)
    ? plannerMinLines
    : getWidowOrphanMinLines();

  /** Clone a header or footer element into a page container as an absolute overlay. */
  function cloneOverlay(el, position, pageContainer) {
    if (!el) return;
    const clone = el.cloneNode(true);
    clone.style.position = 'absolute';
    clone.style.left = '0';
    clone.style.width = PAGE_W + 'px';
    clone.style.zIndex = '10';
    if (position === 'top') {
      clone.style.top = '0';
    } else {
      clone.style.bottom = '0';
    }
    clone.querySelectorAll(PARAGRAPH_TEXT_BLOCK_SELECTOR).forEach((t) => {
      t.style.margin = '0';
    });
    pageContainer.appendChild(clone);
  }

  /** Absolutely position a fragment clone; preserves intrinsic centering (left:50% + translateX). */
  function applyPosition(clone, top, left, width) {
    const inlineLeft = (clone.style.left || '').trim();
    const inlineTransform = (clone.style.transform || '').toLowerCase();
    const preservesIntrinsicCentering =
      inlineLeft.includes('%') &&
      (inlineTransform.includes('translatex(-50%') ||
        inlineTransform.includes('translate(-50%'));

    clone.style.position = 'absolute';
    clone.style.top = top + 'px';
    // Measured `left` already reflects authored horizontal spacing (e.g. indents).
    // Keep that coordinate authoritative in decoupage and avoid re-applying
    // margins after absolute positioning, which can push nodes beyond page bounds.
    clone.style.marginLeft = '0';
    clone.style.marginRight = '0';
    if (!preservesIntrinsicCentering) {
      clone.style.left = left + 'px';
      clone.style.width = width + 'px';
      clone.style.right = 'auto';
    } else {
      // Keep element-authored centering (e.g. left:50% + translateX(-50%)).
      clone.style.right = 'auto';
    }
  }

  /** Measure how tall `el` would be at the given `width` in the iframe. */
  function measureCloneHeight(el, width) {
    const {mClone, cleanup} = createMeasurementClone(
      el,
      width,
      iframeDoc,
      tiptapClassName,
      tiptapInheritedCSS
    );
    if (!mClone) {
      cleanup();
      return 0;
    }
    const h = mClone.getBoundingClientRect().height;
    cleanup();
    return h;
  }

  /**
   * Prefer editor-provided split anchors when available, but fall back to
   * runtime split inference. This preserves split precision without forcing
   * snapshot page-routing decisions that can drift under browser zoom.
   */
  function splitWithOptionalContractAnchor(el, cutHeight, width, contract) {
    return (
      (contract?.splitAnchor
        ? splitAtContractAnchor(el, contract.splitAnchor)
        : null) ||
      splitNodeAtHeight(
        el,
        cutHeight,
        width,
        iframeDoc,
        tiptapClassName,
        tiptapInheritedCSS
      )
    );
  }

  /**
   * Split, then reject the result if it produces a degenerate/overlapping
   * paragraph. Returns null when the split should not be used.
   */
  function safeSplit(el, cutHeight, width, tag, contract) {
    let result = splitWithOptionalContractAnchor(
      el,
      cutHeight,
      width,
      contract
    );
    if (result && isParagraphLikeTag(tag)) {
      const origText = normalizeBoundaryText(el.textContent || '');
      const topText = normalizeBoundaryText(result.topClone.textContent || '');
      const bottomText = normalizeBoundaryText(
        result.bottomClone.textContent || ''
      );
      if (hasRiskySplitOverlap(origText, topText, bottomText)) {
        result = null;
      }
    }
    return result;
  }

  /** List-related tags that can represent boundary-carry semantics. */
  const isListBoundaryTag = (tag) => isListContainerTag(tag) || tag === 'li';

  /** Measure line visibility at a planner cut in iframe coordinate space. */
  function classifyLineVisibilityAtPlannerCut(el, cutHeight, width) {
    return classifyLineVisibilityAtCutInIframe(
      el,
      cutHeight,
      width,
      iframeDoc,
      tiptapClassName,
      tiptapInheritedCSS
    );
  }

  // Plan first, render second. This avoids read/write interleaving where DOM
  // mutations during planning could influence subsequent split decisions.
  const plannedPages = Array.from({length: numPages}, () => []);
  const plannedSourceRefsByPage = Array.from(
    {length: numPages},
    () => new Set()
  );
  let pendingSplitBottoms = [];
  let fragSeq = 0;
  // Tracks nodes where text/list split could not be safely resolved in planner.
  // Why: surfaced in plannerStats to aid parity debugging without mutating flow.
  const unresolvedTextListSplits = [];

  /** Record a planned fragment for a page. Tracks source refs for de-duplication. */
  function pushFragment(pageIdx, fragment) {
    const _frag = {
      id: 'frag_' + fragSeq++,
      targetPage: pageIdx,
      kind: fragment.kind || 'whole',
      sourceNodeId: fragment.sourceNodeId || '',
      sourceNodeKey: fragment.sourceNodeKey || '',
      ...fragment,
    };
    plannedPages[pageIdx].push(_frag);
    const sourceRef = _frag.sourceNodeKey || _frag.sourceNodeId;
    if (sourceRef) {
      plannedSourceRefsByPage[pageIdx].add(sourceRef);
    }
  }

  /** Derive page-local writable bounds from the page structure entry. */
  function getPageWritableBounds(p) {
    const ps = pageStructure[p];
    const headerH = ps?.headerHeight ?? 46;
    const footerH = ps?.footerHeight ?? 46;
    const pageTopY = ps?.pageTopY ?? p * PAGE_H;
    const headerBottomY = ps?.headerBottomY ?? pageTopY + headerH;
    const footerTopY = ps?.footerTopY ?? pageTopY + (PAGE_H - footerH);
    const writableTop = headerBottomY - pageTopY;
    const writableBot = footerTopY - pageTopY;
    return {pageTopY, headerBottomY, footerTopY, writableTop, writableBot};
  }

  /** Check if a non-splitTop fragment already exists for this source on the given page. */
  function hasVisibleNonSplitTopFragment(pageIdx, sourceNodeRef) {
    if (!sourceNodeRef) return false;
    const frags = plannedPages[pageIdx] || [];
    return frags.some((f) => {
      const fragRef = f.sourceNodeKey || f.sourceNodeId;
      if (fragRef !== sourceNodeRef) return false;
      return f.kind !== 'splitTop';
    });
  }

  // ── Phase 1: Plan fragments for each page (no DOM mutations) ──
  // For each page, process in two passes:
  //   Pass A — Carried fragments (pendingSplitBottoms from the previous page):
  //     • split type: content that was split and needs continued rendering
  //     • clip type:  unsplittable overflow rendered via translate offset
  //   Pass B — Source nodes assigned to this page by measureSection:
  //     • Atomic blocks (keepWithNext, images) are carried whole if straddling
  //     • Contract-governed nodes follow the editor's decision (current/next/split)
  //     • Remaining straddlers use inference-based splitting or clip fallback
  for (let p = 0; p < numPages; p++) {
    const {writableTop, writableBot} = getPageWritableBounds(p);
    const writableArea = writableBot - writableTop;
    const nextSplitBottoms = [];
    const pendingMinWithinTop = pendingSplitBottoms.reduce((min, sb) => {
      const v = sb?.sourceWithinTop;
      if (!Number.isFinite(v)) return min;
      return v < min ? v : min;
    }, Number.POSITIVE_INFINITY);
    const hasPendingOffsets = Number.isFinite(pendingMinWithinTop);
    const resolvePendingTop = (sb, fallbackTop = writableTop) => {
      if (!hasPendingOffsets || !Number.isFinite(sb?.sourceWithinTop)) {
        return fallbackTop;
      }
      return writableTop + (sb.sourceWithinTop - pendingMinWithinTop);
    };
    const enqueueSplitBottom = ({
      el,
      left,
      width,
      sourceNodeId = '',
      sourceNodeKey = '',
      sourceWithinTop,
      cloneEl = false,
    }) => {
      nextSplitBottoms.push({
        type: 'split',
        el: cloneEl ? cloneWithScrollPaths(el) : el,
        left,
        width,
        sourceNodeId,
        sourceNodeKey,
        sourceWithinTop,
      });
    };
    const enqueueClipBottom = ({
      node,
      clipAmount,
      sourceNodeId = '',
      sourceNodeKey = '',
      sourceWithinTop,
    }) => {
      nextSplitBottoms.push({
        type: 'clip',
        node,
        clipAmount,
        sourceNodeId,
        sourceNodeKey,
        sourceWithinTop,
      });
    };
    const carryNodeToNextPage = ({
      el,
      left,
      width,
      sourceNodeId = '',
      sourceNodeKey = '',
      sourceWithinTop,
    }) => {
      enqueueSplitBottom({
        el,
        left,
        width,
        sourceNodeId,
        sourceNodeKey,
        sourceWithinTop,
        cloneEl: true,
      });
    };
    const recordUnresolved = (details) => {
      unresolvedTextListSplits.push({page: p, ...details});
    };

    // Plan carried fragments first (they always start below header on next page)
    for (const sb of pendingSplitBottoms) {
      const sbSourceNodeKey = sb.sourceNodeKey || sb.sourceNodeId || '';
      const sbContract = authoritativeNodeByRef?.get(sbSourceNodeKey);
      if (
        sbContract &&
        sbContract.decision === 'next_only' &&
        Number.isInteger(sbContract.pageIdx) &&
        p < sbContract.pageIdx
      ) {
        nextSplitBottoms.push({
          ...sb,
          sourceNodeKey: sbSourceNodeKey,
        });
        continue;
      }
      if (sb.type === 'split') {
        const measH = measureCloneHeight(sb.el, sb.width);
        const sbTag = sb.el.tagName?.toLowerCase();
        const sbIsKeepWithNext = isKeepWithNextLikeNode(sb.el);
        const sbIsImageBlock = isImageLikeNode(sb.el);
        const sbIsTextLike = isTextOrListLikeElement(sb.el);
        if (sbIsKeepWithNext || sbIsImageBlock) {
          // Atomic blocks (keep-with-next, images) are never split across
          // pages. If carried, render whole on the destination page.
          pushFragment(p, {
            el: sb.el,
            top: resolvePendingTop(sb, writableTop),
            left: sb.left,
            width: sb.width,
            kind: sbIsKeepWithNext
              ? 'keepWithNextCarryWhole'
              : 'imageCarryWhole',
            sourceNodeId: sb.sourceNodeId || '',
            sourceNodeKey: sbSourceNodeKey,
          });
          continue;
        }
        const sbTextVis = sbIsTextLike
          ? classifyLineVisibilityAtCutInIframe(
              sb.el,
              writableArea,
              sb.width,
              iframeDoc,
              tiptapClassName,
              tiptapInheritedCSS
            )
          : {
              above: true,
              below: measH > writableArea + PAGE_BOUNDARY_EPSILON_PX,
            };

        const canCarryWholeToFreshPage =
          measH <= writableArea + PAGE_BOUNDARY_EPSILON_PX;
        const shouldCarryFromWidowOrphanInCarry =
          sbIsTextLike &&
          canCarryWholeToFreshPage &&
          widowOrphanMinLines > 1 &&
          sbTextVis?.above &&
          sbTextVis?.below &&
          Number.isFinite(sbTextVis?.aboveLineCount) &&
          Number.isFinite(sbTextVis?.belowLineCount) &&
          (sbTextVis.aboveLineCount < widowOrphanMinLines ||
            sbTextVis.belowLineCount < widowOrphanMinLines);
        if (shouldCarryFromWidowOrphanInCarry) {
          carryNodeToNextPage({
            el: sb.el,
            left: sb.left,
            width: sb.width,
            sourceNodeId: sb.sourceNodeId || '',
            sourceNodeKey: sbSourceNodeKey,
            sourceWithinTop: sb.sourceWithinTop,
          });
          continue;
        }

        // Case 2: all visible content belongs to current page
        if (sbIsTextLike && sbTextVis.above && !sbTextVis.below) {
          pushFragment(p, {
            el: sb.el,
            top: resolvePendingTop(sb, writableTop),
            left: sb.left,
            width: sb.width,
            kind: 'splitCarryWhole',
            sourceNodeId: sb.sourceNodeId || '',
            sourceNodeKey: sbSourceNodeKey,
          });
          continue;
        }

        // Case 3: all visible content belongs to next page
        if (sbIsTextLike && !sbTextVis.above && sbTextVis.below) {
          carryNodeToNextPage({
            el: sb.el,
            left: sb.left,
            width: sb.width,
            sourceNodeId: sb.sourceNodeId || '',
            sourceNodeKey: sbSourceNodeKey,
            sourceWithinTop: sb.sourceWithinTop,
          });
          continue;
        }

        if (measH > writableArea + PAGE_BOUNDARY_EPSILON_PX) {
          const reSplit = splitNodeAtHeight(
            sb.el,
            writableArea,
            sb.width,
            iframeDoc,
            tiptapClassName,
            tiptapInheritedCSS
          );
          if (reSplit) {
            suppressContinuationMarkerAfterListSplit(
              reSplit.topClone,
              reSplit.bottomClone
            );
            pushFragment(p, {
              el: reSplit.topClone,
              top: resolvePendingTop(sb, writableTop),
              left: sb.left,
              width: sb.width,
              kind: 'splitCarryTop',
              sourceNodeId: sb.sourceNodeId || '',
              sourceNodeKey: sbSourceNodeKey,
            });
            enqueueSplitBottom({
              el: reSplit.bottomClone,
              left: sb.left,
              width: sb.width,
              sourceNodeId: sb.sourceNodeId || '',
              sourceNodeKey: sbSourceNodeKey,
              sourceWithinTop: sb.sourceWithinTop,
            });
          } else {
            if (sbIsTextLike) {
              const sbDataId = getElementDataId(sb.el) || sb.sourceNodeId || '';
              recordUnresolved({
                reason: 'carry-resplit-failed',
                tag: sbTag,
                dataId: sbDataId,
              });
              const sourceNodeId = sbDataId;
              if (
                !hasVisibleNonSplitTopFragment(
                  p,
                  sbSourceNodeKey || sourceNodeId
                )
              ) {
                carryNodeToNextPage({
                  el: sb.el,
                  left: sb.left,
                  width: sb.width,
                  sourceNodeId,
                  sourceNodeKey: sbSourceNodeKey || sourceNodeId,
                  sourceWithinTop: sb.sourceWithinTop,
                });
              } else {
                recordUnresolved({
                  reason: 'contract-drop-carry-after-visible',
                  tag: sbTag,
                  dataId: sourceNodeId,
                });
              }
            } else {
              pushFragment(p, {
                el: sb.el,
                top: resolvePendingTop(sb, writableTop),
                left: sb.left,
                width: sb.width,
                kind: 'splitCarryNoResplit',
                sourceNodeId: sb.sourceNodeId || '',
                sourceNodeKey: sbSourceNodeKey,
              });
              enqueueClipBottom({
                node: {
                  el: sb.el,
                  height: measH,
                  left: sb.left,
                  width: sb.width,
                },
                clipAmount: writableArea,
                sourceNodeId: sb.sourceNodeId || '',
                sourceNodeKey: sbSourceNodeKey,
                sourceWithinTop: sb.sourceWithinTop,
              });
            }
          }
        } else {
          pushFragment(p, {
            el: sb.el,
            top: resolvePendingTop(sb, writableTop),
            left: sb.left,
            width: sb.width,
            kind: 'splitCarryWhole',
            sourceNodeId: sb.sourceNodeId || '',
            sourceNodeKey: sbSourceNodeKey,
          });
        }
      } else if (sb.node?.el) {
        const clone = cloneWithScrollPaths(sb.node.el);
        const ca = sb.clipAmount;
        pushFragment(p, {
          el: clone,
          top: resolvePendingTop(sb, writableTop),
          left: sb.node.left,
          width: sb.node.width,
          clipTop: ca,
          kind: 'clipCarry',
          sourceNodeId: getElementDataId(sb.node.el),
          sourceNodeKey: sbSourceNodeKey,
        });
      }
    }

    // Plan source nodes for this page
    for (const node of nodes) {
      if (node.pageIdx !== p) continue;
      if (!node.el) continue;
      const sourceNodeId = getElementDataId(node.el);
      const sourceNodeKey = node.sourceNodeKey || sourceNodeId;
      let forceOverflowSplitForNode = false;
      const isKeepWithNext = isKeepWithNextLikeNode(node.el);
      const isImageBlock = isImageLikeNode(node.el);
      if (sourceNodeKey && plannedSourceRefsByPage[p].has(sourceNodeKey)) {
        recordUnresolved({
          reason: 'duplicate-source-skipped-on-page',
          tag: node.el.tagName?.toLowerCase() || '',
          dataId: sourceNodeId,
        });
        continue;
      }
      const tag = node.el.tagName?.toLowerCase();
      const contract = node.breakContract;
      if (
        (isKeepWithNext || isImageBlock) &&
        node.withinPageBottom > writableBot + PAGE_BOUNDARY_EPSILON_PX
      ) {
        // Atomic blocks (keep-with-next, images) must stay together: carry the
        // whole block instead of splitting when it crosses the page boundary.
        carryNodeToNextPage({
          el: node.el,
          left: node.left,
          width: node.width,
          sourceNodeId,
          sourceNodeKey,
          sourceWithinTop: node.withinPageTop,
        });
        continue;
      }
      // Contract is authoritative when present: this keeps print aligned with
      // editor pagination decisions and limits local inference drift.
      if (
        APPLY_EDITOR_CONTRACT_DECISIONS &&
        contract &&
        contract.sourceNodeKey === sourceNodeKey &&
        typeof contract.decision === 'string'
      ) {
        switch (contract.decision) {
          case 'current_only':
            // Contract is authoritative by default, but we intentionally
            // override `current_only` when iframe geometry proves real overflow.
            // This protects list/text boundary parity when producer contract and
            // print-time geometry diverge (for example, sub-pixel/zoom drift).
            if (
              isTextOrListLikeElement(node.el) &&
              node.withinPageBottom > writableBot + PAGE_BOUNDARY_EPSILON_PX
            ) {
              const overflowDelta = node.withinPageBottom - writableBot;
              const isListBoundary = isListBoundaryTag(tag);
              const contractCurrentOnlyVis =
                node.editorLineVisibility &&
                typeof node.editorLineVisibility.above === 'boolean' &&
                typeof node.editorLineVisibility.below === 'boolean'
                  ? node.editorLineVisibility
                  : classifyLineVisibilityAtPlannerCut(
                      node.el,
                      writableBot - node.withinPageTop,
                      node.width
                    );
              if (
                contractCurrentOnlyVis?.below ||
                (isListBoundary && overflowDelta > PAGE_BOUNDARY_EPSILON_PX)
              ) {
                forceOverflowSplitForNode = true;
                break;
              }
            }
            pushFragment(p, {
              el: cloneWithScrollPaths(node.el),
              top: node.withinPageTop,
              left: node.left,
              width: node.width,
              kind: 'contractCurrentOnly',
              sourceNodeId,
              sourceNodeKey,
            });
            continue;
          case 'next_only':
            // Contract is preferred, but for list containers we may observe
            // visible content above the cut in print-time geometry even when
            // producer emitted `next_only`. In that case, fall through to
            // inference splitting instead of carrying the whole list.
            if (
              isListContainerTag(tag) &&
              node.withinPageBottom > writableBot + PAGE_BOUNDARY_EPSILON_PX
            ) {
              const cutHeight = writableBot - node.withinPageTop;
              const nextOnlyListVis = classifyLineVisibilityAtPlannerCut(
                node.el,
                cutHeight,
                node.width
              );
              if (nextOnlyListVis?.above) {
                forceOverflowSplitForNode = true;
                break;
              }
            }
            carryNodeToNextPage({
              el: node.el,
              left: node.left,
              width: node.width,
              sourceNodeId,
              sourceNodeKey,
              sourceWithinTop: node.withinPageTop,
            });
            continue;
          case 'split': {
            const cutHeight = writableBot - node.withinPageTop;
            let contractAnchorSplit = null;
            if (contract?.splitAnchor) {
              contractAnchorSplit = splitAtContractAnchor(
                node.el,
                contract.splitAnchor
              );
            }
            let contractSplit = contractAnchorSplit;
            if (contractSplit) {
              const topHeight = measureCloneHeight(
                contractSplit.topClone,
                node.width
              );
              const isAnchorTopOverflowingCut =
                topHeight > cutHeight + PAGE_BOUNDARY_EPSILON_PX;
              if (isAnchorTopOverflowingCut) {
                // Guard against stale anchor coordinates (common with nested lists):
                // if anchor top still overflows this page cut, prefer geometric split.
                contractSplit = null;
              }
            }
            if (!contractSplit) {
              contractSplit = splitNodeAtHeight(
                node.el,
                cutHeight,
                node.width,
                iframeDoc,
                tiptapClassName,
                tiptapInheritedCSS
              );
            }

            if (contractSplit) {
              if (tag === 'li' && contractSplit.bottomClone) {
                contractSplit.bottomClone.style.listStyleType = 'none';
              }
              suppressContinuationMarkerAfterListSplit(
                contractSplit.topClone,
                contractSplit.bottomClone
              );
              preserveJustification(node.el, contractSplit.topClone);
              pushFragment(p, {
                el: contractSplit.topClone,
                top: node.withinPageTop,
                left: node.left,
                width: node.width,
                kind: 'contractSplitTop',
                sourceNodeId,
                sourceNodeKey,
              });
              enqueueSplitBottom({
                el: contractSplit.bottomClone,
                left: node.left,
                width: node.width,
                sourceNodeId,
                sourceNodeKey,
                sourceWithinTop: node.withinPageTop,
              });
            } else {
              recordUnresolved({
                reason: 'contract-split-failed',
                tag,
                dataId: sourceNodeId || sourceNodeKey,
              });
              carryNodeToNextPage({
                el: node.el,
                left: node.left,
                width: node.width,
                sourceNodeId,
                sourceNodeKey,
                sourceWithinTop: node.withinPageTop,
              });
            }
            continue;
          }
          default:
            break;
        }
      }

      if (node.withinPageBottom > writableBot + PAGE_BOUNDARY_EPSILON_PX) {
        const cutHeight = writableBot - node.withinPageTop;
        const isTextOrListLike = isTextOrListLikeElement(node.el);
        const lineVis = isTextOrListLike
          ? forceOverflowSplitForNode
            ? // After a forced contract override, re-measure in iframe at the
              // planner cut to avoid reusing stale producer visibility state.
              classifyLineVisibilityAtPlannerCut(node.el, cutHeight, node.width)
            : node.editorLineVisibility &&
                typeof node.editorLineVisibility.above === 'boolean' &&
                typeof node.editorLineVisibility.below === 'boolean'
              ? node.editorLineVisibility
              : classifyLineVisibilityAtPlannerCut(
                  node.el,
                  cutHeight,
                  node.width
                )
          : {above: true, below: true};
        const hasVisibleLine = isTextOrListLike ? lineVis.above : true;
        const isOrphan = isTextOrListLike
          ? !lineVis.above && lineVis.below
          : false;
        // Case 2: node box crosses boundary, but visible text is only on this page.
        if (
          isTextOrListLike &&
          lineVis.above &&
          !lineVis.below &&
          !forceOverflowSplitForNode
        ) {
          // Keep the fast "whole on current page" path unless we explicitly
          // forced overflow splitting for this node earlier.
          pushFragment(p, {
            el: cloneWithScrollPaths(node.el),
            top: node.withinPageTop,
            left: node.left,
            width: node.width,
            kind: 'wholeBoundaryCurrentOnly',
            sourceNodeId,
            sourceNodeKey,
          });
          continue;
        }

        // Case 3: node box starts on this page but visible text starts on next page.
        if (isTextOrListLike && !lineVis.above && lineVis.below) {
          // Case 3: no fully visible line above boundary. Prefer carry unless
          // a safe split can still preserve a visible top fragment.
          // Prefer an explicit split when feasible; this preserves any
          // visible lines that belong on the current page.
          const case3Split = safeSplit(
            node.el,
            cutHeight,
            node.width,
            tag,
            contract
          );
          if (case3Split) {
            suppressContinuationMarkerAfterListSplit(
              case3Split.topClone,
              case3Split.bottomClone
            );
            pushFragment(p, {
              el: case3Split.topClone,
              top: node.withinPageTop,
              left: node.left,
              width: node.width,
              kind: 'splitTop',
              sourceNodeId,
              sourceNodeKey,
            });
            enqueueSplitBottom({
              el: case3Split.bottomClone,
              left: node.left,
              width: node.width,
              sourceNodeId,
              sourceNodeKey,
              sourceWithinTop: node.withinPageTop,
            });
            continue;
          }

          carryNodeToNextPage({
            el: node.el,
            left: node.left,
            width: node.width,
            sourceNodeId,
            sourceNodeKey,
            sourceWithinTop: node.withinPageTop,
          });
          continue;
        }

        const split = safeSplit(node.el, cutHeight, node.width, tag, contract);

        if (isOrphan) {
          carryNodeToNextPage({
            el: node.el,
            left: node.left,
            width: node.width,
            sourceNodeId,
            sourceNodeKey,
            sourceWithinTop: node.withinPageTop,
          });
        } else if (split) {
          suppressContinuationMarkerAfterListSplit(
            split.topClone,
            split.bottomClone
          );
          preserveJustification(node.el, split.topClone);
          pushFragment(p, {
            el: split.topClone,
            top: node.withinPageTop,
            left: node.left,
            width: node.width,
            kind: 'splitTop',
            sourceNodeId,
            sourceNodeKey,
          });
          enqueueSplitBottom({
            el: split.bottomClone,
            left: node.left,
            width: node.width,
            sourceNodeId,
            sourceNodeKey,
            sourceWithinTop: node.withinPageTop,
          });
        } else {
          if (isTextOrListLike) {
            // Never clip text/list content at character boundaries.
            // If splitting fails unexpectedly, carry the full node forward.
            recordUnresolved({
              reason: 'boundary-split-failed',
              tag,
              dataId: getElementDataId(node.el),
              hasVisibleLine,
              lineVis,
              cutHeight: Math.round(cutHeight * 100) / 100,
            });
            if (!hasVisibleNonSplitTopFragment(p, sourceNodeKey)) {
              carryNodeToNextPage({
                el: node.el,
                left: node.left,
                width: node.width,
                sourceNodeId,
                sourceNodeKey,
                sourceWithinTop: node.withinPageTop,
              });
            } else {
              recordUnresolved({
                reason: 'contract-drop-carry-after-visible',
                tag,
                dataId: sourceNodeId,
              });
            }
          } else {
            pushFragment(p, {
              el: cloneWithScrollPaths(node.el),
              top: node.withinPageTop,
              left: node.left,
              width: node.width,
              kind: 'unsplittableOnBoundary',
              sourceNodeId,
              sourceNodeKey,
            });
            enqueueClipBottom({
              node,
              clipAmount: cutHeight,
              sourceNodeId,
              sourceNodeKey,
              sourceWithinTop: node.withinPageTop,
            });
          }
        }
      } else {
        const clone = cloneWithScrollPaths(node.el);
        pushFragment(p, {
          el: clone,
          top: node.withinPageTop,
          left: node.left,
          width: node.width,
          kind: 'whole',
          sourceNodeId,
          sourceNodeKey,
        });
      }
    }

    pendingSplitBottoms = nextSplitBottoms;
  }

  if (pendingSplitBottoms.length > 0) {
    pendingSplitBottoms.forEach((sb) => {
      const el = sb.type === 'split' ? sb.el : sb.node?.el ? sb.node.el : null;
      const tag = el?.tagName?.toLowerCase();
      if (isTextOrListTag(tag)) {
        unresolvedTextListSplits.push({
          page: numPages - 1,
          reason: 'pending-after-last-page',
          tag,
          dataId: getElementDataId(el) || sb.sourceNodeId || '',
        });
      }
    });
  }

  // ── Planner Invariant Checks ──
  // Post-planning validation to catch drift between editor and print output.
  // Three checks run when authoritativeNodeByRef is available:
  //   1. Fragment-before-authoritative: a fragment appears on a page earlier
  //      than the editor assigned it (skipped for carry fragments, which
  //      intentionally land on a later page).
  //   2. First-fragment page mismatch: the first occurrence of a source node
  //      lands on a different page than the editor expected (again, carry
  //      fragments are excluded).
  //   3. OL continuation numbering: when an OL fragment continues on a later
  //      page, verify it has a `start` attribute or a hidden continuation
  //      marker — otherwise numbering would restart from 1.
  //   4. Adjacent-page duplication: detect when the same visible text appears
  //      on two consecutive pages (indicates a degenerate split).
  if (authoritativeNodeByRef && authoritativeNodeByRef.size) {
    const firstFragmentPageBySource = new Map();
    const invariantViolations = [];

    for (let p = 0; p < plannedPages.length; p++) {
      for (const frag of plannedPages[p]) {
        const sourceNodeRef = frag.sourceNodeKey || frag.sourceNodeId || '';
        if (!sourceNodeRef || !authoritativeNodeByRef.has(sourceNodeRef))
          continue;
        const expected = authoritativeNodeByRef.get(sourceNodeRef);
        if (!firstFragmentPageBySource.has(sourceNodeRef)) {
          firstFragmentPageBySource.set(sourceNodeRef, p);
        }
        if (p < expected.pageIdx) {
          invariantViolations.push({
            page: p,
            reason: 'invariant-fragment-before-authoritative-page',
            dataId: frag.sourceNodeId || sourceNodeRef,
            expectedPage: expected.pageIdx,
            actualPage: p,
            kind: frag.kind,
            tag: expected.tag,
          });
        }

        const fragTag = frag.el?.tagName?.toLowerCase();
        if (fragTag === 'ol' && p > expected.pageIdx && frag.el) {
          const directLis = Array.from(frag.el.children || []).filter(
            (c) => c.tagName && c.tagName.toLowerCase() === 'li'
          );
          if (directLis.length) {
            const firstLi = directLis[0];
            const hasHiddenContinuation =
              (firstLi.style?.listStyleType || '').toLowerCase() === 'none';
            const hasStart = frag.el.hasAttribute?.('start') ?? false;
            if (!hasHiddenContinuation && !hasStart) {
              invariantViolations.push({
                page: p,
                reason: 'invariant-ol-continuation-numbering-missing',
                dataId: frag.sourceNodeId || sourceNodeRef,
                expectedPage: expected.pageIdx,
                actualPage: p,
                kind: frag.kind,
                tag: 'ol',
              });
            }
          }
        }
      }
    }

    for (const [sourceNodeRef, expected] of authoritativeNodeByRef.entries()) {
      const firstPage = firstFragmentPageBySource.get(sourceNodeRef);
      if (typeof firstPage === 'number' && firstPage !== expected.pageIdx) {
        // Carry fragments (images, keep-with-next, splits) intentionally land
        // on a later page than the editor's assignment — skip those.
        const firstFrag = (plannedPages[firstPage] || []).find((f) => {
          const ref = f.sourceNodeKey || f.sourceNodeId || '';
          return ref === sourceNodeRef;
        });
        if (firstFrag && CARRY_FRAGMENT_KINDS.has(firstFrag.kind)) continue;

        invariantViolations.push({
          page: firstPage,
          reason: 'invariant-first-fragment-page-mismatch',
          dataId: sourceNodeRef,
          expectedPage: expected.pageIdx,
          actualPage: firstPage,
          tag: expected.tag,
        });
      }
    }

    // Generic adjacent-page duplication check for visible fragments.
    const compactText = (s) => normalizeBoundaryText((s || '').slice(0, 240));
    for (let p = 0; p < plannedPages.length - 1; p++) {
      const curr = plannedPages[p];
      const next = plannedPages[p + 1];
      curr.forEach((cf) => {
        const cfRef = cf.sourceNodeKey || cf.sourceNodeId || '';
        if (!cfRef || cf.kind === 'splitTop') return;
        const cText = compactText(cf.el?.textContent || '');
        if (!cText) return;
        next.forEach((nf) => {
          const nfRef = nf.sourceNodeKey || nf.sourceNodeId || '';
          if (nfRef !== cfRef) return;
          const nText = compactText(nf.el?.textContent || '');
          if (!nText) return;
          if (
            cText === nText ||
            cText.includes(nText) ||
            nText.includes(cText)
          ) {
            const expected = authoritativeNodeByRef.get(cfRef);
            invariantViolations.push({
              page: p + 1,
              reason: 'invariant-duplicate-visible-adjacent',
              dataId: cf.sourceNodeId || cfRef,
              expectedPage: expected?.pageIdx,
              actualPage: p + 1,
              tag: expected?.tag,
            });
          }
        });
      });
    }

    invariantViolations.forEach((v) => unresolvedTextListSplits.push(v));
  }

  // ── Phase 2: Render planned fragments into per-page containers ──
  // Creates a fixed-size `div.print-page` per page, containing a `.tiptapV3`
  // wrapper with absolute-positioned fragment clones. Each fragment gets:
  //   • Clamping: carry fragments are clamped to writableTop unconditionally;
  //     source fragments only when header intrusion exceeds the threshold.
  //   • Continuation marker suppression: LI fragments appearing on a later
  //     page than their first occurrence have their bullet/number hidden.
  //   • Clip rendering: clipCarry fragments use an overflow:hidden host with
  //     a negative translateY to reveal the un-rendered bottom portion.
  // Rendering intentionally does not recompute split decisions; it consumes the
  // Phase-1 plan as-is to keep behavior deterministic and debuggable.
  const containers = [];
  for (let p = 0; p < numPages; p++) {
    const container = iframeDoc.createElement('div');
    container.className = 'print-page';
    container.style.position = 'relative';
    container.style.width = PAGE_W + 'px';
    container.style.height = PAGE_H + 'px';
    container.style.page = isLandscape ? 'landscapePage' : 'portraitPage';
    container.style.clipPath = 'inset(0)';
    container.style.pageBreakInside = 'avoid';
    if (p < numPages - 1) container.style.pageBreakAfter = 'always';

    const contentWrap = iframeDoc.createElement('div');
    contentWrap.className = tiptapClassName;
    contentWrap.style.cssText =
      tiptapInheritedCSS +
      ';position:absolute;top:0;left:0;z-index:5;width:' +
      PAGE_W +
      'px;height:' +
      PAGE_H +
      'px;margin:0;padding:0;background:transparent;';
    // Landscape print rules can apply !important padding/overflow on .tiptapV3.
    // Decoupage uses absolute fragment coordinates in page-local space, so
    // contentWrap must remain an unpadded, unclipped positioning plane.
    contentWrap.style.setProperty('padding-top', '0', 'important');
    contentWrap.style.setProperty('padding-right', '0', 'important');
    contentWrap.style.setProperty('padding-bottom', '0', 'important');
    contentWrap.style.setProperty('padding-left', '0', 'important');
    contentWrap.style.setProperty('min-height', '0', 'important');
    contentWrap.style.setProperty('overflow', 'visible', 'important');
    contentWrap.style.setProperty('width', PAGE_W + 'px', 'important');
    contentWrap.style.setProperty('height', PAGE_H + 'px', 'important');
    container.appendChild(contentWrap);
    const {writableTop} = getPageWritableBounds(p);
    const renderedSourceRefsOnEarlierPages = new Set();
    for (let prev = 0; prev < p; prev++) {
      for (const prevFrag of plannedPages[prev] || []) {
        const prevRef = prevFrag.sourceNodeKey || prevFrag.sourceNodeId || '';
        if (prevRef) renderedSourceRefsOnEarlierPages.add(prevRef);
      }
    }

    for (const frag of plannedPages[p]) {
      const isCarryLikeKind = CARRY_FRAGMENT_KINDS.has(frag.kind);
      const isSourceLikeKind = SOURCE_FRAGMENT_KINDS.has(frag.kind);
      const headerIntrusion = Number.isFinite(frag.top)
        ? writableTop - frag.top
        : 0;
      const shouldClampCarry =
        Number.isFinite(frag.top) && frag.top < writableTop && isCarryLikeKind;
      const shouldClampSource =
        Number.isFinite(frag.top) &&
        isSourceLikeKind &&
        headerIntrusion > SOURCE_HEADER_CLAMP_THRESHOLD_PX;
      const shouldClampToHeader = shouldClampCarry || shouldClampSource;
      const effectiveTop = shouldClampToHeader ? writableTop : frag.top;
      const fragRef = frag.sourceNodeKey || frag.sourceNodeId || '';
      const fragTag = (frag.el?.tagName || '').toLowerCase();
      const isContinuationListFragment =
        !!fragRef && renderedSourceRefsOnEarlierPages.has(fragRef);
      if (isContinuationListFragment) {
        // Only suppress marker for true LI continuation fragments.
        // Do NOT suppress for UL/OL containers, because a list can continue
        // onto a new page at a clean LI boundary where marker must remain.
        if (fragTag === 'li') {
          frag.el.style.listStyleType = 'none';
        }
      }
      if (
        frag.kind === 'clipCarry' &&
        Number.isFinite(frag.clipTop) &&
        frag.clipTop > 0
      ) {
        // Generic continuation rendering for unsplittable overflow:
        // use overflow-hidden host + translated clone (no clip-path dependence).
        const host = iframeDoc.createElement('div');
        host.style.position = 'absolute';
        host.style.top = (effectiveTop || 0) + 'px';
        host.style.left = (frag.left || 0) + 'px';
        host.style.width = (frag.width || 0) + 'px';
        host.style.height = Math.max(0, PAGE_H - (effectiveTop || 0)) + 'px';
        host.style.overflow = 'hidden';
        host.style.margin = '0';

        applyPosition(frag.el, -frag.clipTop, 0, frag.width);
        host.appendChild(frag.el);
        applyScrollPathState(frag.el, frag.el.__printScrollPathStates);
        contentWrap.appendChild(host);
      } else {
        applyPosition(frag.el, effectiveTop, frag.left, frag.width);
        applyScrollPathState(frag.el, frag.el.__printScrollPathStates);
        contentWrap.appendChild(frag.el);
      }
    }

    const ps = pageStructure[p];
    if (ps) {
      cloneOverlay(ps.headerEl, 'top', container);
      cloneOverlay(ps.footerEl, 'bottom', container);
    }
    containers.push(container);
  }

  const plannerStats = {
    unresolvedTextListSplits,
    strictSplitFidelity,
  };

  return {containers, plannerStats};
}

// ─── React Hook ──────────────────────────────────────────────────────────────

/**
 * V2 print hook — "decoupage" approach (see print-v2-pagination-contract.md).
 *
 * Instead of heuristic clearance / straddle / push-back passes, this hook
 * measures every content node in the print iframe, then rebuilds each section
 * as a sequence of fixed-size page containers with absolutely-positioned
 * content.  Content-aware splitting handles text/list straddlers so the PDF
 * matches the editor pixel-for-pixel.
 *
 * High-level flow inside the `print` callback:
 *   1. Set up iframe (CSS variables, font overrides, no-print; optional watermark for decoupage)
 *   2. Wait for export readiness (fonts, images, stable layout)
 *   3. Normalize broken images and Handsontable empty rows
 *   4. Measure each section BEFORE neutralisation
 *   5. Apply editor pagination snapshot (break contracts)
 *   6. Neutralise pagination CSS (hide page-breaks, reset zoom)
 *   7. Build decoupage containers (plan + render)
 *   8. Replace section DOM with page containers
 *   9. Print (or open dev preview popup)
 *
 * API is identical to usePrintDocument (V1) so callers can swap freely.
 *
 * @param {object} options
 * @param {object}   options.documentData        - document record
 * @param {React.Ref} options.contentToPrintRef  - ref to the printable DOM
 * @param {Function} options.onAfterPrint        - called after print completes
 * @param {Function} options.onBeforePrint       - called before print starts
 * @param {Function} options.onPrepareContent    - async content preparation
 * @param {Function} options.onPrintError        - called on error
 * @param {string}   options.watermarkText       - watermark text (`sanitizeWatermarkText` in ./usePrintDocument.utils.js)
 * @param {string}   options.watermarkSvg        - optional custom SVG for `injectWatermark` (same module)
 * @param {object}   options.watermarkConfig     - optional layout overrides; WatermarkConfig typedef in ./usePrintDocument.utils.js
 * @param {boolean}  options.devPreviewOnly      - open popup instead of printing
 * @param {number}   options.exportReadyTimeoutMs - timeout for readiness checks
 * @param {boolean}  options.strictSplitFidelity - when true, collect unresolved text/list split diagnostics; they are logged with `logger.warn` only if `_debug.pagination.telemetryLogs` is enabled
 * @param {Function} options.getPaginationSnapshot - returns editor snapshot
 * @returns {{ showPrintPreparation, beginPrint, endPrint, cancelPrintPreparation }}
 */
const usePrintDocumentV2 = (options = {}) => {
  const {
    documentData,
    contentToPrintRef,
    onAfterPrint,
    onBeforePrint,
    onPrepareContent,
    onPrintError,
    watermarkText = DEFAULT_WATERMARK_TEXT,
    watermarkSvg,
    watermarkConfig,
    devPreviewOnly = process.env.NODE_ENV !== 'production',
    exportReadyTimeoutMs = EXPORT_READY_TIMEOUT_MS,
    strictSplitFidelity = true,
    getPaginationSnapshot,
  } = options;

  const logger = useLogger();
  const debugFlags = useDebugFlags();
  const printTelemetryEnabled = !!debugFlags?.pagination?.telemetryLogs;

  const [state, dispatch] = useReducer(printReducer, initialState);
  const showPrintPreparation = state.phase !== PrintPhase.IDLE;

  // Process guards:
  // - `isPrintProcessActiveRef` prevents late async tasks after cancel/end.
  // - `printTimeoutRef` tracks the deferred preparation timer.
  // - `printStartTimeRef` enforces minimum loading UX duration.
  const printTimeoutRef = useRef(null);
  const isPrintProcessActiveRef = useRef(false);
  const printStartTimeRef = useRef(null);
  const printConfigRef = useRef({
    shouldWatermark: false,
    watermarkText,
    watermarkSvg,
    watermarkConfig,
  });
  const reactToPrintFnRef = useRef(null);

  useEffect(() => {
    // Keep mutable config synchronized without recreating callbacks.
    printConfigRef.current.watermarkText = sanitizeWatermarkText(watermarkText);
    printConfigRef.current.watermarkSvg = watermarkSvg;
    printConfigRef.current.watermarkConfig = watermarkConfig;
  }, [watermarkText, watermarkSvg, watermarkConfig]);

  const endPrint = useCallback(() => {
    // Centralized teardown used on success, failure, and explicit stop.
    isPrintProcessActiveRef.current = false;
    if (printTimeoutRef.current) {
      clearTimeout(printTimeoutRef.current);
      printTimeoutRef.current = null;
    }
    dispatch({type: ActionTypes.RESET});
    printConfigRef.current.shouldWatermark = false;
    onAfterPrint?.();
  }, [onAfterPrint]);

  const cancelPrintPreparation = useCallback(() => {
    // User cancel path before print callback executes.
    isPrintProcessActiveRef.current = false;
    if (printTimeoutRef.current) {
      clearTimeout(printTimeoutRef.current);
      printTimeoutRef.current = null;
    }
    dispatch({type: ActionTypes.RESET});
    printConfigRef.current.shouldWatermark = false;
    onAfterPrint?.();
  }, [onAfterPrint]);

  // ---- useReactToPrint: the print callback where the decoupage pipeline runs.
  //
  // Execution order inside `print`:
  //   1. Shared iframe setup — CSS vars, font overrides, no-print rules; optional
  //      watermark via usePrintDocument.utils.injectWatermark when shouldWatermark is set
  //   2. Export readiness  — wait for fonts, images, and stable layout
  //   3. Content normalize — fix broken images and Handsontable row heights
  //   4. Measure sections  — capture node positions (BEFORE neutralisation)
  //   5. Apply snapshot    — overlay editor pagination decisions (break contracts)
  //   6. Neutralise CSS    — hide page-break sentinels, reset zoom
  //   7. Build containers  — plan fragments + render into fixed-size pages
  //   8. Replace DOM       — swap section children with page containers
  //   9. Print / preview   — call window.print() or open a debug popup
  //
  // The entire body is wrapped in try/catch so that any unexpected error
  // calls endPrint() to reset the UI (prevents stuck "printing" state).
  //
  // Ordering note:
  // - Measurement must happen before neutralisation so captured geometry matches
  //   what users saw in the editor.
  // - Neutralisation then prepares the iframe for final fixed-page rendering.
  const handlePrint = useReactToPrint({
    documentTitle: _.get(documentData, 'record.name'),
    removeAfterPrint: true,
    content: () => contentToPrintRef?.current,
    print: async (printIframe) => {
      try {
        const iframeDoc =
          printIframe.contentDocument || printIframe.contentWindow.document;

        if (iframeDoc.readyState !== 'complete') {
          await new Promise((resolve) =>
            printIframe.contentWindow.addEventListener('load', resolve)
          );
        }

        const sourceRootEl = contentToPrintRef?.current;
        replayScrollStateFromSourceToIframe(sourceRootEl, iframeDoc);

        // ── Step 1: Shared iframe setup ──
        // Copy CSS variables, inject font overrides, no-print rules,
        // border enhancement for Handsontable, and watermark if requested.

        const parentStyles = getComputedStyle(document.documentElement);
        [
          ['--document-font-family', '"Times New Roman", Times, serif'],
          ['--page-height', `${PORTRAIT_PAGE_HEIGHT}px`],
          ['--page-width', `${PORTRAIT_PAGE_WIDTH}px`],
          ['--page-margin-top-bottom', '46px'],
          ['--page-margin-left-right', '46px'],
          ['--line-height', '1.5'],
          ['--list-item-spacing', '0px'],
          ['--widow-orphan-lines', '1'],
        ].forEach(([v, fb]) => {
          iframeDoc.documentElement.style.setProperty(
            v,
            parentStyles.getPropertyValue(v) || fb
          );
        });
        const pageNumberAlignmentFromRoot = parentStyles
          .getPropertyValue('--page-number-alignment')
          .trim();
        const pageNumberAlignment =
          (documentData?.record?.settings?.pagination?.pageNumberAlignment ??
            documentData?.record?.pageNumberAlignment ??
            pageNumberAlignmentFromRoot) ||
          'center';
        const pageNumberAlignItems =
          PAGE_NUMBER_ALIGN_ITEMS_MAP[pageNumberAlignment] ?? 'center';
        iframeDoc.documentElement.style.setProperty(
          '--page-number-alignment',
          pageNumberAlignment
        );
        iframeDoc.documentElement.style.setProperty(
          '--page-number-align-items',
          pageNumberAlignItems
        );

        // font override
        const fontStyle = iframeDoc.createElement('style');
        fontStyle.textContent =
          '*:not(.print-immutable):not(.print-immutable *) { font-family: var(--document-font-family) !important; }';
        iframeDoc.head.appendChild(fontStyle);

        // no-print
        const npStyle = iframeDoc.createElement('style');
        npStyle.textContent = `
        .no-print { display: none !important; }
        [data-no-print="outline"] { outline: none !important; }
        /* Collaboration presence markers must never appear in print/PDF. */
        .collaboration-carets__caret,
        .collaboration-carets__label,
        [class*="collaboration-caret"],
        [class*="collaboration-carets"],
        [data-liveblocks-cursor],
        [data-liveblocks-presence] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
        /* Yjs selection overlays can inject inline background colors. */
        .ProseMirror-yjs-selection,
        [class*="ProseMirror-yjs-selection"] {
          background: transparent !important;
          background-color: transparent !important;
          box-shadow: none !important;
          border-color: transparent !important;
        }
      `;
        iframeDoc.head.appendChild(npStyle);

        // border enhance
        const beStyle = iframeDoc.createElement('style');
        beStyle.textContent = `
        @media print {
          .handsontable td {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            image-rendering: crisp-edges !important;
            shape-rendering: crispEdges !important;
          }
          .handsontable td[style*="border-top: 1pt solid"]    { border-top-color: #0A0A0A !important; }
          .handsontable td[style*="border-bottom: 1pt solid"]  { border-bottom-color: #0A0A0A !important; }
          .handsontable td[style*="border-left: 1pt solid"]    { border-left-color: #0A0A0A !important; }
          .handsontable td[style*="border-right: 1pt solid"]   { border-right-color: #0A0A0A !important; }
          .handsontable td[style*="border-top: 3pt double"],
          .handsontable td[style*="border-top-style: double"]    { border-top-color: #0A0A0A !important; }
          .handsontable td[style*="border-bottom: 3pt double"],
          .handsontable td[style*="border-bottom-style: double"] { border-bottom-color: #0A0A0A !important; }
        }
      `;
        iframeDoc.head.appendChild(beStyle);

        // watermark
        const cfg = printConfigRef.current;
        if (cfg.shouldWatermark) {
          injectWatermark(iframeDoc, {
            text: cfg.watermarkText,
            svg: cfg.watermarkSvg,
            config: cfg.watermarkConfig,
            contentWidth: getContentWidth(),
            contentHeight: getContentHeight(),
          });
        }

        // document title (used by print dialog / PDF defaults)
        const docName = _.get(documentData, 'record.name');
        const prevTitle = document.title;
        if (docName) {
          document.title = docName;
          iframeDoc.title = docName;
        }

        fixBorderBottomZIndex(iframeDoc);

        await new Promise((resolve) => setTimeout(resolve, 100));

        // Constrain viewport to the widest in-document page orientation.
        // Why: if landscape exists but iframe stays portrait-width (816px),
        // some browsers clip the rotated compositor layer to ~8.5in.
        const hasLandscapeSection = !!iframeDoc.querySelector(
          '.document-section.landscape'
        );
        const viewportWidthPx = hasLandscapeSection
          ? PORTRAIT_PAGE_HEIGHT
          : PORTRAIT_PAGE_WIDTH;
        printIframe.style.width = `${viewportWidthPx}px`;
        // Remove iframe scrollbars BEFORE measurement so measured widths are based
        // on the full orientation-specific viewport (not viewport minus gutter).
        const preMeasureViewportStyle = iframeDoc.createElement('style');
        preMeasureViewportStyle.textContent = [
          'html, body { overflow: hidden !important; }',
          'html, html * { scrollbar-width: none !important; }',
          'html::-webkit-scrollbar, html *::-webkit-scrollbar { display: none !important; }',
          // Canonicalize zoom + wrapper transform before readiness/measurement.
          '.editor-zoom-wrapper { transform: none !important; margin-bottom: 0 !important; }',
          '.tiptap-container { --zoom-compensation: 1 !important; }',
          '.document-section { zoom: 1 !important; content-visibility: visible !important; contain-intrinsic-block-size: none !important; }',
        ].join('\n');
        iframeDoc.head.appendChild(preMeasureViewportStyle);
        flushDocumentLayout(iframeDoc);
        const iWin = printIframe.contentWindow;

        const readinessRoot =
          iframeDoc.querySelector('.tiptap-container') || iframeDoc.body;
        const readiness = await waitForExportReadiness({
          iframeDoc,
          iWin,
          root: readinessRoot,
          timeoutMs: exportReadyTimeoutMs,
        });

        if (!readiness.ok) {
          const err = new Error(
            '[V2] Export aborted: content not stable (' + readiness.reason + ')'
          );
          onPrintError?.(err);
          throw err;
        }

        // ── Step 2: Normalize content ──
        // Fix broken images (centering) and Handsontable empty row heights
        // before measuring so positions reflect the final visual layout.
        normalizeIframeContent(iframeDoc, iWin, document);

        // Pull the latest editor snapshot so print consumes the same break
        // contract decisions that editor pagination produced.
        const paginationSnapshot = APPLY_EDITOR_PAGINATION_SNAPSHOT
          ? getPaginationSnapshot?.() || null
          : null;
        const snapshotWidowOrphanMinLines = Number.isFinite(
          paginationSnapshot?.widowOrphanMinLines
        )
          ? paginationSnapshot.widowOrphanMinLines
          : null;

        // ── Step 3: Measure BEFORE neutralisation ──
        // Positions measured here match the UI layout (including page dividers).
        // measureSection accounts for dividers when computing page assignments.
        // Editor pagination snapshots are then overlaid to align print with editor.

        const sections = iframeDoc.querySelectorAll('.document-section');
        const preMeasuredMap = new Map();
        const plannerWarnings = [];
        const iframeWidowOrphanMinLines = (() => {
          if (Number.isFinite(snapshotWidowOrphanMinLines)) {
            return snapshotWidowOrphanMinLines;
          }
          // Read from iframe root so print uses the same effective CSS token
          // that drives layout inside the print document context.
          return getWidowOrphanMinLines(1, iframeDoc.documentElement);
        })();

        sections.forEach((sec, sectionIdx) => {
          let m = measureSection(sec, iWin);
          if (!m) return;

          const sectionId = documentData?.sections?.[sectionIdx]?.id;
          if (APPLY_EDITOR_PAGINATION_SNAPSHOT && paginationSnapshot) {
            const sectionSnapshot = sectionId
              ? paginationSnapshot.sections?.find(
                  (s) => s.sectionId === sectionId
                )
              : paginationSnapshot.sections?.[sectionIdx];
            if (sectionSnapshot) {
              m = applyEditorPaginationSnapshot(m, sectionSnapshot, {
                applyGeometry: APPLY_EDITOR_SNAPSHOT_GEOMETRY && !m.isLandscape,
                applyPageAssignment: false,
              });
            }
          }

          preMeasuredMap.set(sec, m);
        });

        // ── Step 4: Neutralise pagination CSS ──
        // Hide page-break sentinels, reset zoom compensation, and disable
        // CSS containment so the iframe lays out as continuous content.
        const neutStyle = iframeDoc.createElement('style');
        neutStyle.textContent = [
          '.page-break-bottom { page-break-inside: auto !important; break-inside: auto !important; }',
          '.keep-with-next-block { page-break-inside: auto !important; break-inside: auto !important; }',
          '.document-section { content-visibility: visible !important; contain-intrinsic-block-size: none !important; }',
          '.page-break-middle { display: none !important; }',
          // Neutralize viewport wrapper transform together with zoom compensation.
          '.editor-zoom-wrapper { transform: none !important; margin-bottom: 0 !important; }',
          'html, html * { scrollbar-width: none !important; }',
          'html::-webkit-scrollbar, html *::-webkit-scrollbar { display: none !important; }',
          '.tiptap-container { --zoom-compensation: 1 !important; }',
          '.document-section { zoom: 1 !important; }',
          '.page { display: none !important; }',
          '.document-section.landscape .page { margin-top: 0 !important; height: 0 !important; }',
        ].join('\n');
        iframeDoc.head.appendChild(neutStyle);
        flushDocumentLayout(iframeDoc);

        // Re-apply after neutralisation to keep parity in case styles reset.
        normalizeIframeContent(iframeDoc, iWin, document);
        // ── Step 5: Decoupage — build page containers from measurements ──
        // Each section's children are replaced with fixed-size page containers
        // containing absolutely-positioned content fragments.

        const pageAtRule = iframeDoc.createElement('style');
        pageAtRule.textContent = [
          `@page portraitPage { size: ${PORTRAIT_PAGE_WIDTH}px ${PORTRAIT_PAGE_HEIGHT}px; margin: 0; }`,
          `@page landscapePage { size: ${PORTRAIT_PAGE_HEIGHT}px ${PORTRAIT_PAGE_WIDTH}px; margin: 0; }`,
          '@page { margin: 0; }',
        ].join('\n');
        iframeDoc.head.appendChild(pageAtRule);
        // Force style/layout flush so @page rules are active before we collect
        // extracted print rules and build decoupage containers.
        flushDocumentLayout(iframeDoc);

        // extract @media print rules into unconditional sheet
        const printRules = [];
        for (const sheet of iframeDoc.styleSheets) {
          try {
            for (const rule of sheet.cssRules) {
              if (
                rule.type === CSSRule.MEDIA_RULE &&
                rule.media?.mediaText === 'print'
              ) {
                for (const inner of rule.cssRules) {
                  printRules.push(inner.cssText);
                }
              }
            }
          } catch (_e) {
            /* cross-origin — skip */
          }
        }
        if (printRules.length) {
          // Decoupage owns page geometry. Filter legacy landscape print rules
          // that re-apply transform/overflow/page-break behavior and conflict
          // with decoupage containers at print-compositor time.
          const filteredPrintRules = printRules.filter((txt) => {
            const t = txt || '';
            if (t.includes('.document-section.landscape .tiptapV3'))
              return false;
            if (t.includes('.document-section.landscape .page-break'))
              return false;
            if (t.includes('.document-section.landscape .page-break-bottom'))
              return false;
            if (
              t.includes('.document-section.landscape {') &&
              (t.includes('transform:') ||
                t.includes('overflow: hidden') ||
                t.includes('break-after'))
            ) {
              return false;
            }
            return true;
          });
          const ps = iframeDoc.createElement('style');
          ps.textContent = filteredPrintRules.join('\n');
          iframeDoc.head.appendChild(ps);
          flushDocumentLayout(iframeDoc);
        }
        // Re-apply decoupage-critical overrides AFTER extracted print rules so
        // legacy landscape transforms/sizing cannot override fixed page layout.
        const decoupageFinalOverride = iframeDoc.createElement('style');
        decoupageFinalOverride.textContent = [
          // Print compositor workaround:
          // - keep rotated landscape fallback expected by current print flow
          // - force full 11in section box to prevent pre-transform clipping
          // - preserve the legacy 120px landscape offset from DocumentStyles
          //   (`rotate(-90deg) translate(-100%, 120px)`), but apply it as
          //   explicit `left` compensation after we filter conflicting legacy
          //   print rules; otherwise rotated decoupage pages drift left in PDF
          '.document-section.landscape { transform: rotate(-90deg) translate(-100%, 0px) !important; transform-origin: left top !important; width: 1056px !important; max-width: 1056px !important; height: 1056px !important; overflow: visible !important; left: 120px !important; }',
          '.print-page > .tiptapV3, .print-page > .tiptap { width: 100% !important; height: 100% !important; min-height: 0 !important; padding: 0 !important; overflow: visible !important; }',
        ].join('\n');
        iframeDoc.head.appendChild(decoupageFinalOverride);
        flushDocumentLayout(iframeDoc);

        sections.forEach((sec) => {
          const preMeasured = preMeasuredMap.get(sec);
          const decoupage = buildDecoupageContainers(preMeasured, iframeDoc, {
            strictSplitFidelity,
            widowOrphanMinLines: iframeWidowOrphanMinLines,
          });
          if (
            !decoupage ||
            !decoupage.containers ||
            decoupage.containers.length === 0
          ) {
            if (printTelemetryEnabled) {
              logger.warn(
                '[V2] buildDecoupageContainers returned empty for section',
                sec
              );
            }
            return;
          }

          const {containers, plannerStats} = decoupage;
          if (
            plannerStats?.unresolvedTextListSplits &&
            plannerStats.unresolvedTextListSplits.length
          ) {
            const actionableUnresolved =
              plannerStats.unresolvedTextListSplits.filter(
                (u) => !(u?.lineVis && !u.lineVis.above && u.lineVis.below)
              );
            if (strictSplitFidelity && actionableUnresolved.length) {
              const sampleIds = actionableUnresolved
                .slice(0, 5)
                .map((x) => x.dataId || x.tag)
                .join(', ');
              plannerWarnings.push(
                new Error(
                  '[V2] Unresolved text/list split(s): ' +
                    actionableUnresolved.length +
                    '. Sample: ' +
                    sampleIds
                )
              );
            }
          }

          // Replace section children atomically with print-page containers.
          while (sec.firstChild) sec.removeChild(sec.firstChild);
          containers.forEach((c) => sec.appendChild(c));

          sec.style.height = 'auto';
          sec.style.overflow = 'visible';
          sec.style.marginBottom = '0';
        });

        // Avoid trailing blank page: clear page-break-after on last container.
        const allPrintPages = iframeDoc.querySelectorAll('.print-page');
        if (allPrintPages.length > 0) {
          allPrintPages[allPrintPages.length - 1].style.pageBreakAfter = 'auto';
        }
        // Normalize list continuation markers/numbering across page boundaries.
        // Match by list identity + parent-LI context first, then use first/last
        // LI data-id equality to detect true "same-LI continuation" seams.
        const pagesForListNormalization = Array.from(allPrintPages);
        const getDirectLis = (listEl) =>
          Array.from(listEl?.children || []).filter(
            (c) => (c.tagName || '').toLowerCase() === 'li'
          );
        for (let pi = 1; pi < pagesForListNormalization.length; pi++) {
          const currentPage = pagesForListNormalization[pi];
          const currentLists = Array.from(
            currentPage.querySelectorAll('ol, ul')
          );
          for (const currentList of currentLists) {
            const currentTag = (currentList.tagName || '').toLowerCase();
            const currentLIs = getDirectLis(currentList);
            const currentFirstLI = currentLIs[0] || null;
            const currentFirstId =
              currentFirstLI?.getAttribute?.('data-id') || '';
            const currentListId =
              currentList.getAttribute?.('data-id')?.trim?.() || '';
            if (!currentFirstId) continue;
            const currentParentLiId =
              currentList.closest('li')?.getAttribute?.('data-id') || '';

            let matchedPrev = null;
            for (let prevPi = pi - 1; prevPi >= 0 && !matchedPrev; prevPi--) {
              const prevLists = Array.from(
                pagesForListNormalization[prevPi].querySelectorAll('ol, ul')
              );
              for (let li = prevLists.length - 1; li >= 0; li--) {
                const prevList = prevLists[li];
                const prevTag = (prevList.tagName || '').toLowerCase();
                if (prevTag !== currentTag) continue;
                const prevParentLiId =
                  prevList.closest('li')?.getAttribute?.('data-id') || '';
                if (prevParentLiId !== currentParentLiId) continue;
                const prevListId =
                  prevList.getAttribute?.('data-id')?.trim?.() || '';
                const prevLIs = getDirectLis(prevList);
                const prevLastLI = prevLIs[prevLIs.length - 1] || null;
                const prevLastId = prevLastLI?.getAttribute?.('data-id') || '';
                const sameListIdentity =
                  !!currentListId &&
                  !!prevListId &&
                  currentListId === prevListId;
                const sameLiContinuation =
                  !!prevLastId &&
                  !!currentFirstId &&
                  prevLastId === currentFirstId;
                // Prefer strict list identity for seam matching.
                // Fallback to same-LI continuation for older content that may
                // not carry a list-level data-id.
                if (sameListIdentity || sameLiContinuation) {
                  matchedPrev = {prevList, prevLIs, prevPi};
                  break;
                }
              }
            }
            if (!matchedPrev) continue;

            const prevLastLI =
              matchedPrev.prevLIs[matchedPrev.prevLIs.length - 1] || null;
            const prevLastId = prevLastLI?.getAttribute?.('data-id') || '';
            const isSameLiContinuation =
              !!prevLastId && !!currentFirstId && prevLastId === currentFirstId;
            if (isSameLiContinuation) {
              currentFirstLI.style.listStyleType = 'none';
            }
            if (currentTag === 'ol') {
              const prevStart =
                parseInt(matchedPrev.prevList.getAttribute('start'), 10) || 1;
              const expectedStart = Math.max(
                1,
                prevStart +
                  matchedPrev.prevLIs.length -
                  (isSameLiContinuation ? 1 : 0)
              );
              currentList.setAttribute('start', String(expectedStart));
              currentList.style.setProperty(
                'counter-reset',
                `list-item ${expectedStart - 1}`
              );
            }
          }
        }
        flushDocumentLayout(iframeDoc);

        // Decoupage rebuilds sections using cloned fragment nodes. Ensure cloned
        // images are fetched/decoded before snapshotting print compositor output.
        const postDecoupageImageTimeout = Math.max(
          EXPORT_READY_MIN_STAGE_TIMEOUT_MS,
          Math.floor(exportReadyTimeoutMs / 3)
        );
        const finalRenderRoot =
          iframeDoc.querySelector('.tiptap-container') || iframeDoc.body;
        const postDecoupageImagesReady = await waitForPostDecoupageImagesReady({
          root: finalRenderRoot,
          timeoutMs: postDecoupageImageTimeout,
          settleDelayMs: POST_DECOUPAGE_IMAGE_SETTLE_MS,
        });

        if (!postDecoupageImagesReady && printTelemetryEnabled) {
          logger.warn(
            '[V2] Post-decoupage image readiness timed out; PDF may omit delayed image assets.'
          );
        }

        if (plannerWarnings.length && printTelemetryEnabled) {
          // Non-blocking for now: keep exporting while collecting diagnostics.
          plannerWarnings.forEach((w) => {
            logger.warn(w.message);
          });
        }

        if (devPreviewOnly) {
          // Developer inspection mode: open final iframe HTML in popup.
          const popup = window.open(
            '',
            '_blank',
            'width=900,height=700,scrollbars=yes'
          );
          if (popup) {
            popup.document.open();
            popup.document.write(
              '<!DOCTYPE html>' + iframeDoc.documentElement.outerHTML
            );
            popup.document.close();
          }
        }

        if (!devPreviewOnly && printIframe.contentWindow) {
          printIframe.contentWindow.print();
        }

        if (docName) {
          document.title = prevTitle;
        }
      } catch (err) {
        logger.error('[V2] Print pipeline error:', err);
        onPrintError?.(err);
        endPrint();
      }
    },
    onAfterPrint: endPrint,
    bodyClass: document.body.className,
  });

  reactToPrintFnRef.current = handlePrint;

  // Public entry point from UI actions.
  // Lifecycle:
  // 1) mark process active and show prep UI
  // 2) run optional async pre-print content preparation
  // 3) enforce minimum loading duration
  // 4) invoke react-to-print callback (runs decoupage pipeline)
  const beginPrint = useCallback(
    (printOptions = {}) => {
      const {withWatermark = false} = printOptions;

      logger.info('[V2] Printing in V2 mode', {
        withWatermark,
        devPreviewOnly,
      });

      isPrintProcessActiveRef.current = true;
      printConfigRef.current.shouldWatermark = withWatermark;
      printStartTimeRef.current = Date.now();

      dispatch({type: ActionTypes.BEGIN_PRINT});
      onBeforePrint?.();

      printTimeoutRef.current = setTimeout(async () => {
        printTimeoutRef.current = null;
        if (!isPrintProcessActiveRef.current) return;

        try {
          if (onPrepareContent) await onPrepareContent();
        } catch (error) {
          logger.error('[V2] Error during print preparation:', error);
          onPrintError?.(error);
          endPrint();
          return;
        }

        if (!isPrintProcessActiveRef.current) return;

        const elapsed = Date.now() - (printStartTimeRef.current ?? Date.now());
        const remaining = MIN_LOADING_MS - elapsed;
        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, remaining));
        }

        if (!isPrintProcessActiveRef.current) return;

        if (contentToPrintRef?.current) {
          reactToPrintFnRef.current?.();
          isPrintProcessActiveRef.current = false;
          dispatch({type: ActionTypes.RESET});
        } else {
          logger.error('[V2] Print content ref not available');
          endPrint();
        }
      }, 100);
    },
    [onBeforePrint, onPrepareContent, onPrintError, endPrint, logger]
  );

  return {
    showPrintPreparation,
    beginPrint,
    endPrint,
    cancelPrintPreparation,
  };
};

export default usePrintDocumentV2;
